import React from 'react';
import { Box } from '@mui/material';
import SegmentedToggle from '../../../../../Components/SegmentedToggle';
import InfoIconTooltip from '../../../../../Components/Icons/InfoIconTooltip';
import { SohConfidenceHelpContent } from './SohPivotCell';
import SingleSelectDropdown from '../../../../Overview/components/SingleSelectDropdown';
import { PIVOT_CONTROL_HEIGHT } from '../../../../../Features/Table/MonthlyHistoricalPivotTable/MonthlyHistoricalPivotTable';
import {
    ANY_CONFIDENCE,
    SOH_CONFIDENCE_LEVELS,
} from '../../../../../Helper/BatteryHealth/SohHistoryHelper';

/**
 * Filter controls for the SOH monthly history, as the `filters` list the
 * generic MonthlyHistoricalPivotTable renders in its filter card (label above
 * each control). Status uses the product's SegmentedToggle; Entity and
 * Confidence use the product's SingleSelectDropdown (the page-header filter
 * dropdown), with the notched label off since the card puts labels above.
 * Every control is optional (org meta monthlyHistory.filters).
 *
 * statusOptions:  from buildStatusFilterOptions ({ value, label, count }), untranslated
 * entityOptions:  from buildEntityOptions ({ value, label }); labels are the entity
 *                 slots' labels, already translated (shown as is)
 * filters:        { status, entity, confidence }
 * onChange:       (key, value) => void
 */
export const buildSohHistoryFilters = ({
    t,
    showStatus = true,
    showEntity = true,
    showConfidence = true,
    statusOptions = [],
    entityOptions = [],
    filters = {},
    onChange,
}) => {
    // Status labels may come from org meta: translated with the separators off.
    const tLabel = (text) => t(text, { nsSeparator: false, keySeparator: false });
    const statusToggleOptions = statusOptions.map((option) => ({
        value: option.value,
        label: option.count === null || option.count === undefined
            ? tLabel(option.label)
            : `${tLabel(option.label)} (${option.count})`,
    }));
    const confidenceOptions = [
        { value: ANY_CONFIDENCE, label: t('Any') },
        ...SOH_CONFIDENCE_LEVELS.map((level) => ({ value: level, label: t(level) })),
    ];

    return [
        showStatus && {
            key: 'status',
            label: t('Status'),
            node: (
                <SegmentedToggle
                    ariaLabel={t('Status')}
                    options={statusToggleOptions}
                    value={filters.status}
                    onChange={(value) => onChange?.('status', value)}
                    testIdPrefix="soh-history-status-filter"
                    height={PIVOT_CONTROL_HEIGHT}
                />
            ),
        },
        showEntity && {
            key: 'entity',
            label: t('Entity'),
            node: (
                <SingleSelectDropdown
                    label={t('Entity')}
                    options={entityOptions}
                    selectedOption={filters.entity}
                    onChange={(value) => onChange?.('entity', value)}
                    dataTestId="soh-history-entity-filter"
                    hideBoxLabel={true}
                    height={PIVOT_CONTROL_HEIGHT}
                />
            ),
        },
        showConfidence && {
            key: 'confidence',
            label: t('Confidence'),
            // Same pattern as the generic table's filter label help (FilterBody's
            // FilterLabelTooltip): an info icon with a rich tooltip.
            help: (
                <Box data-testid="soh-history-confidence-help-icon" sx={{ display: 'flex' }}>
                    <InfoIconTooltip title={<SohConfidenceHelpContent />} maxWidth={320} placement="right" />
                </Box>
            ),
            node: (
                <SingleSelectDropdown
                    label={t('Confidence')}
                    options={confidenceOptions}
                    selectedOption={filters.confidence}
                    onChange={(value) => onChange?.('confidence', value)}
                    dataTestId="soh-history-confidence-filter"
                    hideBoxLabel={true}
                    height={PIVOT_CONTROL_HEIGHT}
                    searchable={false}
                />
            ),
        },
    ].filter(Boolean);
};
